import React, { useState } from 'react'
import { ControlGroup, MenuItem, Position } from '@blueprintjs/core'
import { ItemRenderer, Select2 } from '@blueprintjs/select'
import { DateRangeInput } from '@blueprintjs/datetime'
import { Button, IconButton } from '@ui'
import moment from 'moment'
import { datePickerFormatter } from '@utils'

const DateSelect = Select2<IDateRangePreset>

export interface IDateRangePreset {
  label: string
  fromDate: Date | undefined
  toDate: Date | undefined
}

interface Props {
  label: string
  prefix: string
  name: string
  value: IDateRangePreset | null
  defaultVal?: IDateRangePreset | null
  onChange: (dateRange: IDateRangePreset | null) => void
}

export function isDefault(name: string, val: IDateRangePreset | null) {
  if (!val || name !== 'endDate') return false
  return moment(val.fromDate).isSame(moment(), 'day') && !val.toDate
}

const dateRanges: IDateRangePreset[] = []
for (const i of [3, 6, 9, 12, 18]) {
  dateRanges.push({
    label: `Within ${i} months`,
    fromDate: moment().toDate(),
    toDate: moment().add(i, 'months').toDate(),
  })
}
dateRanges.push({
  label: 'Custom Range',
  fromDate: new Date(),
  toDate: new Date(),
})

export default function DateFilter({
  defaultVal = null,
  value,
  name,
  onChange,
  label,
  prefix,
}: Props) {
  const isNotCustom =
    !value ||
    (!value.fromDate && !value.toDate) ||
    isDefault(name, value) ||
    dateRanges.find(
      (d) =>
        moment(d.fromDate).isSame(value.fromDate, 'day') &&
        moment(d.toDate).isSame(value.toDate, 'day')
    )
  const [custom, setCustom] = useState(!isNotCustom)

  const renderDate: ItemRenderer<IDateRangePreset> = (
    dateRange,
    { handleClick, modifiers, query }
  ) => {
    return modifiers.matchesPredicate ? (
      <MenuItem
        active={modifiers.active}
        key={dateRange.label}
        onClick={handleClick}
        text={dateRange.label}
      />
    ) : null
  }

  const clearButton =
    Boolean(value) && !isDefault(name, value) ? (
      <IconButton
        minimal
        className="ml1"
        round
        tooltip="Clear"
        iconName="times"
        onClick={() => {
          onChange(defaultVal)
          setCustom(false)
        }}
      />
    ) : null

  const formatDate = (date: Date | null) => (date ? moment(date).format('MM/DD') : 'No Date')

  // use null instead of undefined to make blueprint typescript happy
  const fromDate: Date | null = value?.fromDate || null
  const toDate: Date | null = value?.toDate || null

  if (custom)
    return (
      <>
        <DateRangeInput
          {...datePickerFormatter('MM/DD/YY')}
          value={[fromDate, toDate] as [any, any]}
          popoverProps={{ minimal: true, position: Position.BOTTOM }}
          onChange={(range) => {
            const label = `${formatDate(range[0] as Date | null)}-${formatDate(
              range[1] as Date | null
            )}`
            onChange({
              label,
              fromDate: range[0] as Date | undefined,
              toDate: range[1] as Date | undefined,
            })
          }}
        />
        {clearButton}
      </>
    )

  return (
    <ControlGroup>
      <DateSelect
        popoverProps={{ minimal: true, position: Position.BOTTOM }}
        filterable={false}
        items={dateRanges}
        onItemSelect={(item) => {
          if (item.label === 'Custom Range') return setCustom(true)
          onChange(item)
        }}
        itemRenderer={renderDate}
      >
        <Button text={(value && value.label) || label} rightIconName={'chevron-down'} />
      </DateSelect>
      {clearButton}
    </ControlGroup>
  )
}
